import React, { useState, useEffect } from 'react';
import { Card, Select, Button, Table, Spin, message } from 'antd';
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import apiClient from '../../../utils/apiClient';
import { downloadCSV } from '../../../utils/csvExport';

const { Option } = Select;

interface CouponTemplate {
  id: number;
  title: string;
}

interface ProductData {
  product_name: string;
  product_id: string;
  purchase_count: number;
  total_quantity: number;
  avg_price: number;
}

const ProductAnalytics: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<CouponTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState('all');
  const [data, setData] = useState<ProductData[]>([]);

  useEffect(() => {
    fetchTemplates();
    fetchData();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await apiClient.get('/coupon-analytics/analytics/templates');
      if (response.data.success) {
        setTemplates(response.data.data);
      }
    } catch (error) {
      console.error('テンプレート取得エラー:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (selectedTemplate !== 'all') {
        params.template_id = selectedTemplate;
      }

      const response = await apiClient.get('/coupon-analytics/analytics/concurrent-products', { params });

      if (response.data.success) {
        setData(response.data.data);
      } else {
        message.error('データ取得に失敗しました');
      }
    } catch (error) {
      console.error('併用購入商品分析エラー:', error);
      message.error('データ取得エラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    const csvData = data.map(row => ({
      '商品名': row.product_name,
      '購入回数': row.purchase_count,
      '総販売数量': row.total_quantity,
      '平均単価': row.avg_price ? `¥${Math.round(row.avg_price).toLocaleString()}` : '-'
    }));

    downloadCSV(csvData, '併用購入商品分析.csv');
    message.success('CSVファイルをダウンロードしました');
  };

  const columns = [
    {
      title: '順位',
      key: 'rank',
      render: (_: any, __: any, index: number) => (
        <div className="flex items-center">
          {index === 0 && <span className="text-2xl mr-2">🥇</span>}
          {index === 1 && <span className="text-2xl mr-2">🥈</span>}
          {index === 2 && <span className="text-2xl mr-2">🥉</span>}
          <span className="font-bold">{index + 1}</span>
        </div>
      ),
      width: 100
    },
    {
      title: '商品名',
      dataIndex: 'product_name',
      key: 'product_name',
      ellipsis: true
    },
    {
      title: '購入回数',
      dataIndex: 'purchase_count',
      key: 'purchase_count',
      align: 'right' as const,
      render: (count: number) => (
        <span className="font-bold text-blue-600">{count}回</span>
      ),
      sorter: (a: ProductData, b: ProductData) => a.purchase_count - b.purchase_count,
      defaultSortOrder: 'descend' as const,
      width: 120
    },
    {
      title: '総販売数量',
      dataIndex: 'total_quantity',
      key: 'total_quantity',
      align: 'right' as const,
      render: (quantity: number) => `${quantity}個`,
      width: 120
    },
    {
      title: '平均単価',
      dataIndex: 'avg_price',
      key: 'avg_price',
      align: 'right' as const,
      render: (price: number) => price ? `¥${Math.round(price).toLocaleString()}` : '-',
      width: 120
    }
  ];

  return (
    <div>
      {/* フィルター */}
      <Card className="mb-4">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              クーポン絞り込み
            </label>
            <Select
              value={selectedTemplate}
              onChange={setSelectedTemplate}
              style={{ width: '100%' }}
            >
              <Option value="all">全てのクーポン</Option>
              {templates.map(template => (
                <Option key={template.id} value={template.id}>
                  {template.title}
                </Option>
              ))}
            </Select>
          </div>

          <div className="flex items-end gap-2">
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={fetchData}
              loading={loading}
            >
              検索
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExportCSV}
              disabled={data.length === 0}
            >
              CSV出力
            </Button>
          </div>
        </div>
      </Card>

      {/* 説明カード */}
      <Card className="mb-4 bg-blue-50">
        <div className="flex items-start gap-3">
          <div className="text-2xl">💡</div>
          <div>
            <div className="font-bold text-gray-800 mb-1">併用購入商品分析とは？</div>
            <div className="text-sm text-gray-600">
              クーポン使用時に一緒に購入された商品のランキングです。
              どの商品がクーポン利用者に人気かを把握できます。
            </div>
          </div>
        </div>
      </Card>

      {/* データテーブル */}
      <Card title={`人気商品TOP10 ${selectedTemplate !== 'all' ? '（絞り込み中）' : ''}`}>
        <Spin spinning={loading}>
          {data.length > 0 ? (
            <Table
              columns={columns}
              dataSource={data}
              rowKey="product_id"
              pagination={false}
              locale={{
                emptyText: '該当するデータがありません'
              }}
            />
          ) : (
            <div className="text-center text-gray-500 py-12">
              {loading ? 'データ読み込み中...' : 'データがありません'}
            </div>
          )}
        </Spin>
      </Card>
    </div>
  );
};

export default ProductAnalytics;
